/**
 * useTemplateForm Hook
 *
 * Purpose: Core form state management hook for the TemplateForm component.
 *          Fetches view configuration, builds field registry, manages form values/
 *          errors/touched state, wires template hooks, and handles validation + submit flow.
 * Architecture: Custom React hook using react-query for data fetching, React state for
 *               form management, and memoized callbacks for stable references.
 *               Mirrors patterns from metadata-form/useMetadataForm.ts.
 * Dependencies: React (useState, useEffect, useCallback, useMemo, useRef),
 *               @tanstack/react-query (useQuery),
 *               metadataService (getModelView),
 *               ./types (ViewField, ViewConfiguration, FormData, FormActions, FormState, etc.)
 * Author: Development Team
 * Created: 2026-02-15
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { asList, SINGLE_SELECT_ERROR } from '../../utils/listValues';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { metadataService } from '../../services/metadataService';
import { mdmCacheKeys } from '../../services/mdmCacheService';
import { metadataViewQueryKey } from '../../hooks/useModelFieldLabels';
import { usePickerFilterCheck } from '../../hooks/usePickerFilterCheck';
import type { PermissionKey } from '../../constants/permissionKeys';
import type { MasterDataValue } from '../../types/mdm';
import type {
  ViewField,
  ViewConfiguration,
  FormData,
  FormActions,
  FormTemplate,
  FormState,
  TemplateFormContextValue,
  IndicatorMode,
} from './types';

/**
 * UseTemplateFormOptions — configuration for the useTemplateForm hook
 *
 * Purpose: Defines all inputs needed to initialize and manage a template form
 */
interface UseTemplateFormOptions {
  template: FormTemplate;
  tenantId: string;
  mode?: 'create' | 'edit';
  initialData?: Record<string, any>;
  entityId?: string;
  onSubmit?: (data: FormData) => Promise<void>;
  onChange?: (data: FormData) => void;
  readOnly?: boolean;
  loading?: boolean;
  indicatorMode?: IndicatorMode;
  appliedFieldKeys?: Set<string>;
  fieldResults?: Record<string, import('./types').FieldMatchResult>;
  /** ORCH-400: the permission the form's save exercises, for the server-side value check */
  pickerPermission?: PermissionKey | null;
}

/**
 * UseTemplateFormReturn — return value of the useTemplateForm hook
 *
 * Purpose: Provides all data and functions needed by TemplateForm to render
 *          and manage the form lifecycle
 */
export interface UseTemplateFormReturn {
  contextValue: TemplateFormContextValue;
  formState: FormState;
  formActions: {
    submit: () => Promise<void>;
    reset: () => void;
    validate: () => boolean;
  };
  /** Same rules as submit validation, without mutating errors (for gating Maestro pipeline). */
  checkSubmitReadiness: () => boolean;
  viewLoading: boolean;
  viewError: string | null;
  /** ORCH-400: fielddef_id -> stored ids this user can't remove, for <PickerFilters> */
  pickerLocked: Record<string, string[]>;
}

/**
 * useTemplateForm — core form state management hook
 *
 * Purpose: Manages the full lifecycle of a template-driven form: fetches view config,
 *          builds field registry, manages values/errors/touched, wires template hooks,
 *          validates fields, and handles the submit flow
 * Logic: 1) Fetches view configuration via react-query
 *        2) Builds a Map<fieldDefId, ViewField> registry from config
 *        3) Manages form state (values, errors, touched, isSubmitting, isDirty)
 *        4) Provides FormActions for reading/writing values and errors
 *        5) Validates using metadata rules + template custom validation
 *        6) Submit flow: validateAll → hooks.beforeSubmit → onSubmit callback
 * Input: UseTemplateFormOptions — template, tenantId, mode, initialData, callbacks
 * Output: UseTemplateFormReturn — contextValue, formState, formActions, loading/error
 */
export const useTemplateForm = (options: UseTemplateFormOptions): UseTemplateFormReturn => {
  const { template, tenantId, initialData, onSubmit, onChange } = options;

  // Fetch view configuration
  const {
    data: viewConfig,
    isLoading: viewLoading,
    error: viewError,
  } = useQuery<ViewConfiguration>({
    queryKey: metadataViewQueryKey(template.modelId, template.viewType, tenantId),
    queryFn: async () => {
      const response = await metadataService.getModelView(
        template.modelId,
        tenantId,
        template.viewType as 'edit-modal' | 'table' | 'list' | 'detail'
      );
      return response as unknown as ViewConfiguration;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!template.modelId && !!tenantId,
  });

  // Build field registry
  const fieldRegistry = useMemo(() => {
    const map = new Map<string, ViewField>();
    if (viewConfig?.fields) {
      viewConfig.fields.forEach((f) => {
        map.set(f.fielddef_id, f);
      });
    }
    return map;
  }, [viewConfig]);

  // Placed fields tracking — uses a ref to collect registrations without
  // triggering per-field re-renders, then a single batched state update
  // so ExtraFields re-renders once with the complete set
  const placedFieldsRef = useRef<Set<string>>(new Set());
  const [placedFields, setPlacedFields] = useState<Set<string>>(() => new Set());
  const batchPending = useRef(false);

  const markPlaced = useCallback((id: string) => {
    if (placedFieldsRef.current.has(id)) return;
    placedFieldsRef.current.add(id);
    // Batch: schedule a single state update after all synchronous registrations
    if (!batchPending.current) {
      batchPending.current = true;
      Promise.resolve().then(() => {
        batchPending.current = false;
        setPlacedFields(new Set(placedFieldsRef.current));
      });
    }
  }, []);

  // Form state
  const [values, setValues] = useState<Record<string, any>>(() => {
    const init: Record<string, any> = {};
    if (initialData?.metadata) {
      Object.assign(init, initialData.metadata);
    }
    if (template.defaultValues) {
      // Default values applied under initial data
      Object.keys(template.defaultValues).forEach((key) => {
        if (init[key] === undefined) {
          init[key] = template.defaultValues![key];
        }
      });
    }
    return init;
  });
  const [ownErrors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planningCurrencyCode, setPlanningCurrencyCode] = useState<string | null>(null);
  const initCalled = useRef(false);
  const queryClient = useQueryClient();

  // ORCH-400 §4.6: the server says which selected list values would be refused on save
  // (security scope, enabled lists, related/subset filters). They are a separate error
  // source: setValue's clear-on-change and validateAll's reset don't wipe them, and only
  // the next answer does. Fields they name are marked touched so a saved mismatch shows
  // as the record opens.
  const listFieldIds = useMemo(
    () =>
      [...fieldRegistry.values()]
        .filter((f) => f.field_definition?.datatype === 'list')
        .map((f) => f.fielddef_id),
    [fieldRegistry]
  );
  const { errors: filterErrors, locked: pickerLocked } = usePickerFilterCheck({
    modelId: template.modelId,
    tenantId,
    values,
    listFieldIds,
    permission: options.pickerPermission ?? null,
    recordId: options.mode === 'edit' ? options.entityId : null,
    enabled: !options.readOnly,
  });
  const filterErrorsRef = useRef(filterErrors);
  useEffect(() => {
    filterErrorsRef.current = filterErrors;
    const ids = Object.keys(filterErrors);
    if (ids.length) {
      setTouched((prev) => {
        if (ids.every((id) => prev[id])) return prev;
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = true;
        });
        return next;
      });
    }
  }, [filterErrors]);
  const errors = useMemo(() => ({ ...filterErrors, ...ownErrors }), [filterErrors, ownErrors]);

  // Recompute isDirty
  const isDirty = useMemo(() => {
    const initMeta = initialData?.metadata || {};
    return Object.keys(values).some((key) => values[key] !== initMeta[key]);
  }, [values, initialData]);

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  // Form actions
  const getValue = useCallback((id: string) => values[id], [values]);

  const setValue = useCallback(
    (id: string, value: any) => {
      setValues((prev) => ({ ...prev, [id]: value }));
      setTouched((prev) => ({ ...prev, [id]: true }));
      // Clear error on change
      setErrors((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      // Fire hook
      if (template.hooks?.onFieldChange) {
        // Defer so state is updated, use actionsRef for latest state
        setTimeout(() => {
          template.hooks!.onFieldChange!(id, value, actionsRef.current);
        }, 0);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template.hooks]
  );

  const setMultipleValues = useCallback(
    (vals: Record<string, any>) => {
      setValues((prev) => ({ ...prev, ...vals }));
      // Fire the hook for every field set, exactly as setValue does for single
      // edits (ORCH-377). Without this, programmatic fills (Maestro drafts) skip
      // the template's onFieldChange and dependent state — e.g. the planning
      // currency behind the Budget symbol — never updates.
      if (template.hooks?.onFieldChange) {
        // Defer so state is updated, use actionsRef for latest state
        setTimeout(() => {
          Object.entries(vals).forEach(([id, value]) => {
            template.hooks!.onFieldChange!(id, value, actionsRef.current);
          });
        }, 0);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template.hooks]
  );

  const setError = useCallback((id: string, message: string) => {
    setErrors((prev) => ({ ...prev, [id]: message }));
  }, []);

  const clearError = useCallback((id: string) => {
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const getData = useCallback((): FormData => {
    return { metadata: { ...values } };
  }, [values]);

  const getFieldMeta = useCallback((id: string) => fieldRegistry.get(id), [fieldRegistry]);

  /**
   * getSelectedOption — retrieves the full MDM option object for a selected list field value
   *
   * Purpose: Allows template hooks to access MDM metadata (e.g., meta_data.fd_code)
   *          for a currently selected list field value
   * Logic: Reads from React Query cache using the field's tenant_id and fielddef_id,
   *        then finds the option matching the current value UUID
   * Input: fieldDefId — the field definition ID (e.g., 'fd_currency')
   * Output: MasterDataValue object or undefined if not found/not cached
   */
  const getSelectedOption = useCallback(
    (fieldDefId: string): MasterDataValue | undefined => {
      const currentValue = values[fieldDefId];
      if (!currentValue) return undefined;
      const field = fieldRegistry.get(fieldDefId);
      if (!field) return undefined;
      const cachedData = queryClient.getQueryData<MasterDataValue[]>(
        mdmCacheKeys.values(fieldDefId, tenantId)
      );
      if (!cachedData) return undefined;
      return cachedData.find((opt) => opt.id === currentValue);
    },
    [values, fieldRegistry, queryClient, tenantId]
  );

  const actions: FormActions = useMemo(
    () => ({
      getValue,
      setValue,
      setValues: setMultipleValues,
      setError,
      clearError,
      getData,
      getFieldMeta,
      getSelectedOption,
      setPlanningCurrency: setPlanningCurrencyCode,
    }),
    [
      getValue,
      setValue,
      setMultipleValues,
      setError,
      clearError,
      getData,
      getFieldMeta,
      getSelectedOption,
    ]
  );

  // Ref to always hold the latest actions — used by deferred hook calls (setTimeout)
  // so they read current state instead of a stale closure capture
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  /**
   * validateField — validates a single field value against metadata rules
   *
   * Purpose: Checks required constraint and datatype-specific validation
   * Logic: Checks required → then datatype-specific rules (number, date, list, boolean)
   * Input: field (ViewField metadata), value (current field value)
   * Output: Error message string or null if valid
   */
  const validateField = useCallback((field: ViewField, value: any): string | null => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    if (
      field.is_required &&
      (trimmedValue === null || trimmedValue === undefined || trimmedValue === '')
    ) {
      return `${field.label_override || field.field_definition.label} is required`;
    }
    if (value === null || value === undefined || value === '') return null;

    const datatype = field.field_definition.datatype;
    switch (datatype.toLowerCase()) {
      case 'currency':
      case 'decimal':
      case 'number':
        if (isNaN(value)) return 'Must be a valid number';
        break;
      case 'date':
      case 'datetime':
        if (!(value instanceof Date) && isNaN(Date.parse(value))) return 'Must be a valid date';
        break;
      case 'list':
        // Either shape is accepted and the server stores the declared one (ORCH-354);
        // only a single-select field holding several values is an error
        if (!field.field_definition.is_multiselect && asList(value).length > 1) {
          return SINGLE_SELECT_ERROR;
        }
        break;
      case 'bool':
      case 'boolean':
        if (typeof value !== 'boolean') return 'Must be true or false';
        break;
    }
    return null;
  }, []);

  /**
   * validateAll — validates all fields in the registry plus custom template validation
   *
   * Purpose: Runs full form validation before submit
   * Logic: Iterates field registry for metadata validation, then merges template hooks.validate()
   * Input: None (reads fieldRegistry, values, template.hooks from closure)
   * Output: boolean — true if no errors, false if validation fails
   */
  const validateAll = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    const newTouched: Record<string, boolean> = {};

    // Metadata validation
    fieldRegistry.forEach((field, id) => {
      const error = validateField(field, values[id]);
      if (error) {
        newErrors[id] = error;
        newTouched[id] = true;
      }
    });

    // Template custom validation
    if (template.hooks?.validate) {
      const customErrors = template.hooks.validate({ metadata: { ...values } });
      Object.entries(customErrors).forEach(([id, msg]) => {
        newErrors[id] = msg;
        newTouched[id] = true;
      });
    }

    // ORCH-400: a value the server would refuse blocks the save like a missing required field
    Object.keys(filterErrors).forEach((id) => {
      newTouched[id] = true;
    });

    setErrors(newErrors);
    setTouched((prev) => ({ ...prev, ...newTouched }));
    return Object.keys(newErrors).length === 0 && Object.keys(filterErrors).length === 0;
  }, [fieldRegistry, values, validateField, template.hooks, filterErrors]);

  const checkSubmitReadiness = useCallback((): boolean => {
    if (fieldRegistry.size === 0) return false;
    if (Object.keys(filterErrors).length > 0) return false;
    for (const [id, field] of fieldRegistry.entries()) {
      const err = validateField(field, values[id]);
      if (err) return false;
    }
    if (template.hooks?.validate) {
      const customErrors = template.hooks.validate({ metadata: { ...values } });
      if (customErrors && Object.keys(customErrors).length > 0) return false;
    }
    return true;
  }, [fieldRegistry, values, validateField, template.hooks, filterErrors]);

  /**
   * submit — executes the full form submission flow
   *
   * Purpose: Validates, runs beforeSubmit hook, then calls onSubmit callback
   * Logic: validateAll → hooks.beforeSubmit(data) → onSubmit(data)
   * Input: None (reads from closure)
   * Output: Promise<void>
   * Exceptions: Re-throws errors from beforeSubmit or onSubmit; always resets isSubmitting
   */
  const submit = useCallback(async () => {
    const valid = validateAll();
    if (!valid) return;

    setIsSubmitting(true);
    try {
      const processedValues = { ...values };

      // Convert file field values ({ file_ids, has_files }) to boolean for backend
      fieldRegistry.forEach((field, fieldId) => {
        if (field.field_definition.datatype === 'file' && fieldId in processedValues) {
          const val = processedValues[fieldId];
          if (val && typeof val === 'object' && 'has_files' in val) {
            processedValues[fieldId] = val.has_files;
          }
        }
        // Multiselect list fields must submit arrays. Orchestrator mapping
        // approvals and older draft snapshots can leave a single scalar in form
        // state, which the backend MetadataProcessor rejects with
        // "Multiselect field must be a list" — coerce scalar → [scalar].
        if (
          field.field_definition.datatype === 'list' &&
          field.field_definition.is_multiselect &&
          fieldId in processedValues
        ) {
          const val = processedValues[fieldId];
          if (val !== null && val !== undefined && val !== '' && !Array.isArray(val)) {
            processedValues[fieldId] = [val];
          }
        }
      });

      let data: FormData = { metadata: processedValues };

      if (template.hooks?.beforeSubmit) {
        data = await template.hooks.beforeSubmit(data);
      }

      if (onSubmit) {
        await onSubmit(data);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [validateAll, values, template.hooks, onSubmit, fieldRegistry]);

  /**
   * reset — resets form to initial state
   *
   * Purpose: Restores values to initialData + defaultValues, clears errors and touched
   * Logic: Rebuilds initial values from initialData.metadata and template.defaultValues
   * Input: None (reads initialData, template.defaultValues from closure)
   * Output: void — updates state directly
   */
  const reset = useCallback(() => {
    const init: Record<string, any> = {};
    if (initialData?.metadata) Object.assign(init, initialData.metadata);
    if (template.defaultValues) {
      Object.keys(template.defaultValues).forEach((key) => {
        if (init[key] === undefined) init[key] = template.defaultValues![key];
      });
    }
    setValues(init);
    setErrors({});
    setTouched({});
  }, [initialData, template.defaultValues]);

  // Call onInit hook once when view loads
  useEffect(() => {
    if (viewConfig && !initCalled.current && template.hooks?.onInit) {
      initCalled.current = true;
      template.hooks.onInit(viewConfig.fields, actions);
    }
  }, [viewConfig, template.hooks, actions]);

  // onChange callback
  useEffect(() => {
    if (onChange) {
      onChange({ metadata: { ...values } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  // PR-F (gist N4): hold `values` in a ref so getFieldValidationStatus has a stable
  // callback identity across keystrokes. Without this, every input edit changed `values`,
  // re-created this callback, re-created `contextValue`, and re-rendered every consumer
  // of TemplateFormContext — including dumb leaves that don't read validation status.
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const getFieldValidationStatus = useCallback(
    (id: string): 'invalid' | 'valid' => {
      const field = fieldRegistry.get(id);
      if (!field) return 'invalid';
      if (filterErrorsRef.current[id]) return 'invalid';
      const currentValues = valuesRef.current;
      if (validateField(field, currentValues[id])) return 'invalid';
      if (template.hooks?.validate) {
        const customErrors = template.hooks.validate({ metadata: { ...currentValues } });
        if (customErrors && customErrors[id]) return 'invalid';
      }
      return 'valid';
    },
    [fieldRegistry, validateField, template.hooks]
  );

  const contextValue: TemplateFormContextValue = useMemo(
    () => ({
      fieldRegistry,
      placedFields,
      markPlaced,
      values,
      errors,
      touched,
      actions,
      loading: viewLoading || !!options.loading,
      error: viewError ? String(viewError) : null,
      planningCurrencyCode,
      entityId: options.entityId,
      readOnly: options.readOnly,
      getFieldValidationStatus,
      indicatorMode: options.indicatorMode ?? 'none',
      appliedFieldKeys: options.appliedFieldKeys,
      fieldResults: options.fieldResults,
    }),
    [
      fieldRegistry,
      placedFields,
      markPlaced,
      values,
      errors,
      touched,
      actions,
      viewLoading,
      options.loading,
      viewError,
      planningCurrencyCode,
      options.entityId,
      options.readOnly,
      getFieldValidationStatus,
      options.indicatorMode,
      options.appliedFieldKeys,
      options.fieldResults,
    ]
  );

  const formState: FormState = useMemo(
    () => ({ values, errors, touched, isDirty, isSubmitting, isValid }),
    [values, errors, touched, isDirty, isSubmitting, isValid]
  );

  return {
    contextValue,
    formState,
    formActions: { submit, reset, validate: validateAll },
    checkSubmitReadiness,
    viewLoading,
    viewError: viewError ? String(viewError) : null,
    pickerLocked,
  };
};
